import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../supabaseClient'
import { UserRole } from '../../types'

interface UserEntry {
  id: string
  email: string
  created_at: string
  last_sign_in_at: string | null
  profile: { name: string; role: UserRole; subject: string; primary_grade: number | null } | null
}

async function callManageUsers(action: string, params: Record<string, unknown> = {}): Promise<unknown> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session) throw new Error('Not authenticated')

  const res = await supabase.functions.invoke('manage-users', {
    body: { action, ...params },
  })

  // supabase.functions.invoke returns { data, error }
  // On non-2xx, error is set but data may also contain the JSON body
  if (res.error) {
    // Try to extract the error message from the response body
    const bodyError = res.data?.error
    throw new Error(bodyError || res.error.message)
  }
  if (res.data?.error) throw new Error(res.data.error)
  return res.data
}

const ROLE_COLORS: Record<string, string> = {
  admin: '#dc2626',
  researcher: '#2563eb',
  expert: '#0369a1',
  teacher: '#16a34a',
}

export default function AdminPage() {
  const [users, setUsers] = useState<UserEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteRole, setInviteRole] = useState<UserRole>('researcher')
  const [inviting, setInviting] = useState(false)

  // Edit dialog
  const [editUser, setEditUser] = useState<UserEntry | null>(null)
  const [editName, setEditName] = useState('')
  const [editRole, setEditRole] = useState<UserRole>('researcher')
  const [saving, setSaving] = useState(false)

  // Delete confirm
  const [deleteUser, setDeleteUser] = useState<UserEntry | null>(null)
  const [deleting, setDeleting] = useState(false)

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true)
      const data = (await callManageUsers('list')) as { users: UserEntry[] }
      setUsers(data.users)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return
    setInviting(true)
    setError(null)
    try {
      await callManageUsers('invite', {
        email: inviteEmail.trim(),
        name: inviteName.trim() || undefined,
        role: inviteRole,
      })
      setSuccess(`Invitation email sent to ${inviteEmail}`)
      setInviteEmail('')
      setInviteName('')
      setInviteRole('researcher')
      await loadUsers()
      setTimeout(() => setSuccess(null), 15000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user')
    } finally {
      setInviting(false)
    }
  }

  const openEdit = (u: UserEntry) => {
    setEditUser(u)
    setEditName(u.profile?.name || u.email?.split('@')[0] || '')
    setEditRole(u.profile?.role || 'researcher')
  }

  const handleSaveEdit = async () => {
    if (!editUser) return
    setSaving(true)
    setError(null)
    try {
      await callManageUsers('update_role', {
        userId: editUser.id,
        role: editRole,
      })
      setUsers((prev) => {
        return prev.map((u) =>
          u.id === editUser.id && u.profile ? { ...u, profile: { ...u.profile, role: editRole } } : u,
        )
      })
      setEditUser(null)
      setSuccess('User updated')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteUser) return
    setDeleting(true)
    setError(null)
    try {
      await callManageUsers('delete', { userId: deleteUser.id })
      setUsers((prev) => prev.filter((u) => u.id !== deleteUser.id))
      setDeleteUser(null)
      setSuccess('User deleted')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      <Box sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 800, color: 'primary.main' }}>
          Users
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {users.length} user{users.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 10 }}>
        {/* Invite form */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 12,
                color: 'text.secondary',
                mb: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Add User
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <TextField
                label="Email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                size="small"
                sx={{ flex: 2, minWidth: 180 }}
              />
              <TextField
                label="Name"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 120 }}
              />
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Role</InputLabel>
                <Select value={inviteRole} label="Role" onChange={(e) => setInviteRole(e.target.value as UserRole)}>
                  <MenuItem value="researcher">Researcher</MenuItem>
                  <MenuItem value="expert">Expert (reviewer)</MenuItem>
                  <MenuItem value="teacher">Teacher</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                onClick={handleInvite}
                disabled={inviting || !inviteEmail.trim()}
              >
                {inviting ? <CircularProgress size={16} color="inherit" /> : 'Add'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 1.5 }}>
            {success}
          </Alert>
        )}

        {/* User list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          users.map((u) => {
            const role = u.profile?.role || 'researcher'
            const name = u.profile?.name || u.email?.split('@')[0]
            return (
              <Card key={u.id} variant="outlined" sx={{ mb: 0.75 }}>
                <CardContent sx={{ py: '10px !important', px: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'text.primary' }}>{name}</Typography>
                      <Chip
                        label={role}
                        size="small"
                        sx={{
                          bgcolor: ROLE_COLORS[role] + '15',
                          color: ROLE_COLORS[role],
                          fontWeight: 700,
                          fontSize: 9,
                          height: 18,
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{u.email}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => openEdit(u)} sx={{ color: 'text.secondary' }}>
                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleteUser(u)} sx={{ color: '#dc2626' }}>
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </CardContent>
              </Card>
            )
          })
        )}
      </Box>

      {/* Edit dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Edit User</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            size="small"
            fullWidth
            disabled
          />
          <TextField label="Email" value={editUser?.email || ''} size="small" fullWidth disabled />
          <FormControl size="small" fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={editRole} label="Role" onChange={(e) => setEditRole(e.target.value as UserRole)}>
              <MenuItem value="researcher">Researcher</MenuItem>
              <MenuItem value="expert">Expert (reviewer)</MenuItem>
              <MenuItem value="teacher">Teacher</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditUser(null)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)} maxWidth="xs">
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Delete User</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            Permanently delete <strong>{deleteUser?.profile?.name || deleteUser?.email}</strong>? This removes their
            account and all associated data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteUser(null)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={16} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
